import { useState } from "react";
import { Paperclip, X, Download, Loader2 } from "lucide-react";
import { downloadForumAttachment, type ForumAttachment } from "../api/forumApi";

export function humanSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Papers on a post.
 *
 * Each one is a button rather than a link: the file is served by an
 * authenticated route on the private disk, and an <a href> would send no
 * bearer token and come back 401. The fetch-and-blob dance lives in
 * downloadForumAttachment.
 */
export function AttachmentList({ items }: { items: ForumAttachment[] }) {
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!items?.length) return null;

  const open = async (a: ForumAttachment) => {
    setBusy(a.id);
    setError(null);
    try {
      await downloadForumAttachment(a.url, a.filename);
    } catch (e: any) {
      setError(e?.message || "That file could not be opened.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        {items.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => open(a)}
            disabled={busy === a.id}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left hover:border-gray-300 transition-colors disabled:opacity-60"
          >
            {busy === a.id ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0" />
            ) : (
              <Download className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            )}
            <span className="text-xs font-bold text-gray-700 truncate max-w-[220px]">
              {a.filename}
            </span>
            <span className="text-[10px] font-bold text-gray-400 shrink-0">
              {humanSize(a.size)}
            </span>
          </button>
        ))}
      </div>
      {error && <p className="text-xs text-rose-600 mt-2">{error}</p>}
    </div>
  );
}

/** The file picker on a composer, with the chosen files listed underneath. */
export function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label
        className={`inline-flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 ${
          disabled ? "opacity-50" : "cursor-pointer hover:border-gray-400"
        }`}
      >
        <Paperclip className="w-3.5 h-3.5" />
        Attach papers
        <input
          type="file"
          multiple
          disabled={disabled}
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            // Five is what the server accepts; saying so here beats a 422.
            onChange([...files, ...picked].slice(0, 5));
            e.target.value = "";
          }}
        />
      </label>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => (
            <span
              key={`${f.name}-${i}`}
              className="inline-flex items-center gap-2 rounded-xl bg-gray-50 border border-gray-200 px-3 py-1.5"
            >
              <span className="text-xs font-bold text-gray-700 truncate max-w-[200px]">
                {f.name}
              </span>
              <span className="text-[10px] font-bold text-gray-400">
                {humanSize(f.size)}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="text-gray-400 hover:text-rose-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <p className="text-[10px] text-gray-400 mt-2">
        Up to 5 files, 10 MB each. Papers here are private to the committee —
        they are not served from public storage and every download is checked.
      </p>
    </div>
  );
}
